/**
 * Auth context — minimal JWT-based session.
 *
 * Token lives in localStorage and is auto-attached to every axios request
 * via the global default `Authorization: Bearer ...` header. v1 has no
 * refresh flow; the 30-day token from the backend is sufficient.
 *
 * Anonymous flows (generate-styles, checkout) still work — the backend
 * uses an optional dependency that tags `user_id` only when a valid token
 * is present.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "sp_auth_token";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const attachAxiosToken = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Rehydrate from localStorage on mount + verify the token via /me.
  useEffect(() => {
    const t = localStorage.getItem(STORAGE_KEY);
    if (!t) {
      setLoading(false);
      return;
    }
    attachAxiosToken(t);
    axios
      .get(`${API}/auth/me`)
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        attachAxiosToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const signup = useCallback(async ({ identifier, password, name }) => {
    const res = await axios.post(`${API}/auth/signup`, { identifier, password, name });
    localStorage.setItem(STORAGE_KEY, res.data.token);
    attachAxiosToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const login = useCallback(async ({ identifier, password }) => {
    const res = await axios.post(`${API}/auth/login`, { identifier, password });
    localStorage.setItem(STORAGE_KEY, res.data.token);
    attachAxiosToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    attachAxiosToken(null);
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, signup, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
};
